import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, ChevronRight, ShieldAlert, Check, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DeleteClientButton } from "@/components/DeleteClientButton";

function scoreColor(s) {
  if (s >= 740) return "text-emerald-400";
  if (s >= 670) return "text-blue-400";
  if (s >= 580) return "text-amber-400";
  if (s > 0) return "text-red-400";
  return "text-muted-foreground";
}

export default function Clients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMaster = user?.role === "master";
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", state: "", zip: "", ssn_last4: "", dob: "", credit_score: "", goals: "" });

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = { ...form, credit_score: parseInt(form.credit_score) || 0 };
      const { data } = await api.post("/clients", payload);
      toast.success("Client added");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", address: "", city: "", state: "", zip: "", ssn_last4: "", dob: "", credit_score: "", goals: "" });
      navigate(`/clients/${data.id}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filtered = clients.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || (c.email || "").toLowerCase().includes(q.toLowerCase()));
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageClamped = Math.min(page, totalPages);
  const paged = filtered.slice((pageClamped - 1) * PER_PAGE, pageClamped * PER_PAGE);

  return (
    <div className="animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-primary font-bold mb-2">Client Portfolio</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none">Clients</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-lg" data-testid="add-client-btn"><Plus className="h-4 w-4 mr-1" /> Add Client</Button>
          </DialogTrigger>
          <DialogContent className="bg-card/90 backdrop-blur-xl border-border max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">New Client</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Full Name *" v={form.name} set={(v) => setForm({ ...form, name: v })} testid="client-name" />
              <Field label="Credit Score" v={form.credit_score} set={(v) => setForm({ ...form, credit_score: v })} type="number" testid="client-score" />
              <Field label="Email" v={form.email} set={(v) => setForm({ ...form, email: v })} testid="client-email" />
              <Field label="Phone" v={form.phone} set={(v) => setForm({ ...form, phone: v })} testid="client-phone" />
              <Field label="Date of Birth" v={form.dob} set={(v) => setForm({ ...form, dob: v })} placeholder="MM/DD/YYYY" testid="client-dob" />
              <Field label="SSN (last 4)" v={form.ssn_last4} set={(v) => setForm({ ...form, ssn_last4: v })} maxLength={4} testid="client-ssn" />
              <div className="sm:col-span-2"><Field label="Street Address" v={form.address} set={(v) => setForm({ ...form, address: v })} placeholder="123 Main St" testid="client-address" /></div>
              <Field label="City" v={form.city} set={(v) => setForm({ ...form, city: v })} testid="client-city" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="State" v={form.state} set={(v) => setForm({ ...form, state: v })} placeholder="CA" maxLength={2} testid="client-state" />
                <Field label="ZIP" v={form.zip} set={(v) => setForm({ ...form, zip: v })} placeholder="90001" maxLength={10} testid="client-zip" />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Goals</Label>
                <Textarea value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} placeholder="e.g. Buy a home in 12 months, start a business…" data-testid="client-goals" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} className="rounded-lg" data-testid="save-client-btn">{saving ? "Saving…" : "Create Client"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isMaster && <PendingErasures onResolved={load} />}

      <div className="relative mb-4 max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search clients…" className="pl-9" data-testid="client-search" />
      </div>

      <div className="grid gap-3">
        {paged.map((c) => (
          <Card key={c.id} onClick={() => navigate(`/clients/${c.id}`)} className="p-4 bg-card/60 border-border cursor-pointer hover:border-primary/40 hover:-translate-y-0.5 transition-all flex items-center justify-between" data-testid={`client-card-${c.id}`}>
            <div className="flex items-center gap-4 min-w-0">
              <div className="h-11 w-11 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold shrink-0">{c.name?.[0]?.toUpperCase()}</div>
              <div className="min-w-0">
                <div className="font-medium truncate">{c.name}</div>
                <div className="text-xs text-muted-foreground truncate">{c.email || "No email"} · {c.negative_count} negative item{c.negative_count === 1 ? "" : "s"} · {c.agreements_signed ?? 0}/{c.agreements_total ?? 4} agreements</div>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right">
                <div className={`font-mono text-2xl font-bold ${scoreColor(c.credit_score)}`}>{c.credit_score || "—"}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Score</div>
              </div>
              <DeleteClientButton client={c} onDeleted={() => load()} />
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </Card>
        ))}
        {!filtered.length && <p className="text-sm text-muted-foreground py-12 text-center">No clients found. Add one to begin.</p>}
      </div>

      {filtered.length > PER_PAGE && (
        <div className="flex items-center justify-between mt-6" data-testid="clients-pagination">
          <p className="text-xs text-muted-foreground">Showing {(pageClamped - 1) * PER_PAGE + 1}–{Math.min(pageClamped * PER_PAGE, filtered.length)} of {filtered.length}</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-lg" disabled={pageClamped <= 1} onClick={() => setPage(pageClamped - 1)} data-testid="clients-prev-page">Previous</Button>
            <span className="text-xs text-muted-foreground px-1">Page {pageClamped} / {totalPages}</span>
            <Button variant="outline" size="sm" className="rounded-lg" disabled={pageClamped >= totalPages} onClick={() => setPage(pageClamped + 1)} data-testid="clients-next-page">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, v, set, type = "text", placeholder, maxLength, testid }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input type={type} value={v} onChange={(e) => set(e.target.value)} placeholder={placeholder} maxLength={maxLength} data-testid={testid} />
    </div>
  );
}

function PendingErasures({ onResolved }) {
  const [reqs, setReqs] = useState([]);
  const load = () => api.get("/deletion-requests").then((r) => setReqs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const resolve = async (id, action) => {
    try {
      await api.post(`/deletion-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Client erased" : "Request denied");
      load();
      onResolved?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  if (!reqs.length) return null;
  return (
    <Card className="p-4 mb-5 border-amber-500/40 bg-amber-500/5" data-testid="pending-erasures">
      <div className="flex items-center gap-2 font-display font-semibold mb-3 text-amber-200"><ShieldAlert className="h-4 w-4" /> Erase Requests Awaiting Your Approval ({reqs.length})</div>
      <div className="space-y-2">
        {reqs.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg bg-background/40 px-3 py-2 flex-wrap" data-testid={`erase-req-${r.id}`}>
            <div className="min-w-0 text-sm">
              <span className="font-medium">{r.client_name}</span>
              <span className="text-muted-foreground"> · requested by {r.requested_by}{r.reason ? ` · "${r.reason}"` : ""}</span>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" className="rounded-lg text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10" onClick={() => resolve(r.id, "approve")} data-testid={`erase-approve-${r.id}`}><Check className="h-4 w-4 mr-1" /> Approve</Button>
              <Button size="sm" variant="outline" className="rounded-lg text-red-400 border-red-500/40 hover:bg-red-500/10" onClick={() => resolve(r.id, "deny")} data-testid={`erase-deny-${r.id}`}><X className="h-4 w-4 mr-1" /> Deny</Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
